import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import { filesize } from 'filesize';

import useTableAutoCompleteFilter from '@totem/components/common/filterAutoComplete/TableFilter';
import DeviceBackupContext from '@totem/components/services/backup/deviceBackups/deviceBackupContext';
import {
  BackupRecord,
  BackupReportResponse,
} from '@totem/components/services/backup/deviceBackups/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { DeviceLastBackupFilterOptions } from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import controlSystemUtilities from '@totem/utilities/controlSystemsUtilities';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

const csvEscape = (val: unknown): string => {
  const str = val === null || val === undefined ? '' : String(val);
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const getBackupStatusLabel = (record: BackupRecord): string => {
  if (record.contractService === 'Backup Archival') {
    return 'Archived Only';
  }
  if (
    typeof record.lastBackedUpAt === 'undefined' ||
    record.lastBackedUpAt === null ||
    dayjs(record.lastBackedUpAt).year() < 2000
  ) {
    return 'Not Backed Up';
  }
  if (record.isBackupStale) {
    return 'Stale';
  }
  return 'Current';
};

const buildCsv = (
  records: BackupRecord[],
  includeContractLine: boolean,
): string => {
  const headers = [
    'Device',
    'Control System',
    'Control System Type',
    'Region',
    'Building',
    'Date',
    'Time',
    'Backup Size',
    'Backup Status',
    'Last Backup Status',
  ];
  if (includeContractLine) {
    headers.push('Contract Line');
  }

  const rows = records.map((rec) => {
    const csType =
      controlSystemUtilities.controlSystemTypeMap[rec.controlSystemType];
    const hasDate =
      rec.lastBackedUpAt !== null && dayjs(rec.lastBackedUpAt).year() > 2000;
    const dateVal = hasDate
      ? new Date(rec.lastBackedUpAt).toLocaleDateString()
      : '';
    const timeVal = hasDate
      ? new Date(rec.lastBackedUpAt).toLocaleTimeString()
      : '';
    const sizeVal = rec.lastBackupSize
      ? filesize(rec.lastBackupSize, { standard: 'jedec' })
      : '';

    const cols = [
      rec.displayName,
      rec.controlSystemName,
      csType ? csType.text : '',
      rec.regionName,
      rec.buildingName,
      dateVal,
      timeVal,
      sizeVal,
      getBackupStatusLabel(rec),
      rec.lastBackupStatus ?? '',
    ];
    if (includeContractLine) {
      const contractLine =
        rec.contractNumber !== null &&
        rec.contractNumber !== '' &&
        rec.contractLineNumber !== null
          ? `${rec.contractNumber}:${rec.contractLineNumber}`
          : '';

      cols.push(contractLine);
    }
    return cols.map(csvEscape).join(',');
  });

  return [headers.map(csvEscape).join(','), ...rows].join('\n');
};

export const containerConfiguration: FilterField[] = [
  {
    key: 'displayName',
    label: 'Device',
    sortField: 'device.displayName',
    filterField: '',
    multiple: false,
    values: [],
  },
  {
    key: 'deviceId',
    label: 'Device',
    sortField: '',
    filterField: 'deviceId',
    multiple: true,
    values: [],
  },
  {
    key: 'controlSystem',
    label: 'Control System',
    sortField: 'controlSystem.name',
    filterField: 'controlSystemId',
    multiple: true,
    values: [],
  },
  {
    key: 'systemType',
    label: 'Control System Type',
    sortField: 'controlSystem.systemType',
    filterField: 'controlSystemType',
    multiple: true,
    values: [],
  },
  {
    key: 'size',
    label: 'Backup Size',
    sortField: 'lastBackup.size',
    filterField: '',
    multiple: false,
    values: [],
  },
  {
    key: 'isBackupStale',
    label: 'Backup Status',
    sortField: 'lastBackup.backedUpAt',
    filterField: 'isBackupStale',
    multiple: true,
    values: [],
  },
  {
    key: 'building',
    label: 'Building',
    sortField: 'building.name',
    filterField: 'buildingId',
    multiple: true,
    values: [],
  },
  {
    key: 'region',
    label: 'Region',
    sortField: 'region.name',
    filterField: 'regionId',
    multiple: true,
    values: [],
  },
  {
    key: 'lastBackupStatus',
    label: 'Last Backup Status',
    sortField: '',
    filterField: 'lastBackupStatus',
    multiple: true,
    values: [],
  },
];

type Props = {
  children?: ReactNode;
  onTotalRecordsChanged?: (total: number) => void;
};

const DeviceBackupContainer = ({ children, onTotalRecordsChanged }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    getFilterValues,
    getFilterOptions,
    setFilterOptions,
    handleFilter,
    isReady,
  } = useContext(InputContext);

  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [backupData, setBackupData] = useState<BackupReportResponse>(null);
  const fetchAbortController = useRef<AbortController | null>(null);
  const filterOptionsLoaded = useRef<boolean>(false);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    userProfile.organization.id,
    isReady,
  ]);

  const rawRegionOptions = getFilterOptions('region');
  const regionOptions = useMemo(
    () =>
      rawRegionOptions.map((opt) => ({
        name: String(opt.text),
        value: opt.value,
      })),
    [rawRegionOptions],
  );

  const rawBuildingOptions = getFilterOptions('building');
  const buildingOptions = useMemo(
    () =>
      rawBuildingOptions.map((opt) => ({
        name: String(opt.text),
        value: opt.value,
      })),
    [rawBuildingOptions],
  );

  const rawDeviceOptions = getFilterOptions('deviceId');
  const deviceOptions = useMemo(
    () =>
      rawDeviceOptions.map((opt) => ({
        name: String(opt.text),
        value: opt.value,
      })),
    [rawDeviceOptions],
  );

  const regionFilter = useTableAutoCompleteFilter(
    getFilterValues('region'),
    regionOptions,
    {
      onChange: (newRegion) => {
        const current = getFilterValues('region');
        const same =
          current.length === newRegion.length &&
          newRegion.every((val) => current.includes(val));
        if (!same) {
          handleFilter('region', newRegion);
        }
      },
    },
  );

  const buildingFilter = useTableAutoCompleteFilter(
    getFilterValues('building'),
    buildingOptions,
    {
      onChange: (newBuilding) => {
        const current = getFilterValues('building');
        const same =
          current.length === newBuilding.length &&
          newBuilding.every((val) => current.includes(val));
        if (!same) {
          handleFilter('building', newBuilding);
        }
      },
    },
  );

  const deviceFilter = useTableAutoCompleteFilter(
    getFilterValues('deviceId'),
    deviceOptions,
    {
      onChange: (newDeviceId) => {
        const current = getFilterValues('deviceId');
        const same =
          current.length === newDeviceId.length &&
          newDeviceId.every((val) => current.includes(val));
        if (!same) {
          handleFilter('deviceId', newDeviceId);
        }
      },
    },
  );

  useEffect(() => {
    if (filterOptionsLoaded.current) return;
    filterOptionsLoaded.current = true;

    fetch(`${DEVICES_ENDPOINT}/lastBackupReportFilterOptions`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: DeviceLastBackupFilterOptions) => {
        setFilterOptions(
          'region',
          result.region.map((opt) => ({ text: opt.name, value: opt.value })),
        );

        setFilterOptions(
          'building',
          result.building.map((opt) => ({ text: opt.name, value: opt.value })),
        );

        setFilterOptions(
          'deviceId',
          result.device.map((opt) => ({ text: opt.name, value: opt.value })),
        );

        setFilterOptions(
          'systemType',
          result.controlSystemType.map((opt) => ({
            text: opt.name,
            value: opt.value,
          })),
        );
      });
  }, [getApiToken, setFilterOptions]);

  useEffect(() => {
    if (!refreshData) return;

    fetchAbortController.current?.abort();

    const controller = new AbortController();
    fetchAbortController.current = controller;

    setRefreshData(false);

    setIsLoading(true);

    fetch(`${DEVICES_ENDPOINT}/backupReport`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(buildParameters()),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((result: BackupReportResponse) => {
        setBackupData(result);
        if (isNotNull(onTotalRecordsChanged)) {
          try {
            onTotalRecordsChanged(result.paging.totalRecords);
          } catch {
            /* empty */
          }
        }
      })
      .then(() => {
        setIsLoading(false);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') {
          setIsLoading(false);
        }
      });
  }, [refreshData, getApiToken]);

  const handleExport = () => {
    if (isExporting) return;

    setIsExporting(true);
    const exportParams: Params = {
      ...buildParameters(),
      paging: { page: 1, pageSize: 1000 },
    };
    fetch(`${DEVICES_ENDPOINT}/backupReport`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(exportParams),
    })
      .then((res) => res.json())
      .then((result: BackupReportResponse) => {
        const includeContractLine =
          permissions.contractsViewer.hasPermissions(userProfile);
        const csv = buildCsv(result.items ?? [], includeContractLine);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;

        link.download = 'backup-report.csv';

        link.click();

        URL.revokeObjectURL(url);
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  return (
    <DeviceBackupContext.Provider
      value={{
        backupData,
        loading: isLoading,
        buildingFilter,
        regionFilter,
        deviceFilter,
        onExport: handleExport,
        isExporting,
      }}
    >
      {children}
    </DeviceBackupContext.Provider>
  );
};

export default DeviceBackupContainer;
